import React, { lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { Button } from 'react-bootstrap';
import { setClassNames } from '@ddc/classnames-js';
import Skeleton from 'wsm-skeleton';
import { useLabels, usePrefs, useRequestData } from 'ws-scripts/modules/common';
import { addImageParams } from '../../../utilities/images';
import { truncateGallery } from '../../../utilities/truncateGallery';
import { setModalSettings } from '../../../features/modalSettings';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import { COMPARE_BUTTON_VARIANTS } from '../../../global-constants';
import VehicleImage from './VehicleImage';
import Callout from '../Callout';
import CompareButton from '../CompareButton';
import './Slick.scss';
import './MediaContainer.scss';
import { selectIsTabbedLayout } from '../../../utilities/layout';
import { getVehicleObj } from '../../../utilities/vehicleTracking';

const LazyVehicleImageCarousel = lazy(() => import('./VehicleImageCarousel'));

const Media = ({
	enableMediaCarousel,
	vehicle,
	accountPhone,
	loadingDelayed,
	willRenderBlankPlaceholderImage,
	shouldShowCompareOverImage
}) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const { deviceType, widgetName, windowId } = useRequestData();
	const {
		showVideoModalButton,
		openVideoModalLabel,
		restrictInsertLocationsForVehicles
	} = usePrefs();
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehicleMediaLocation = getVehicleLocationName(
		'vehicle-media',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const { videos = [], uuid, title, link, images: rawImages = [] } = vehicle;

	const { images, wasTruncated } = truncateGallery(rawImages);

	const phone = accountPhone;

	let videoButton = null;
	const isShowVideoModalButton =
		showVideoModalButton === 'true' && videos.length > 0;
	if (isShowVideoModalButton) {
		videoButton = (
			<Button
				bsSize={deviceType === 'DESKTOP' ? 'xsmall' : null}
				className={setClassNames([
					'open-media-modal-btn',
					deviceType === 'MOBILE' ? 'text-nowrap' : '',
					deviceType === 'DESKTOP' ? 'd-block float-right' : ''
				])}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Video button',
						result: 'Video modal displayed',
						vehicle: getVehicleObj(uuid)
					});

					dispatch(
						setModalSettings({
							isModalOpen: true,
							videos,
							uuid,
							title,
							link,
							phone
						})
					);
				}}
			>
				<i className="ddc-icon ddc-icon-play2 mr-2" />
				<span className="text-link text-link-muted">
					{labels.get(openVideoModalLabel)}
				</span>
			</Button>
		);
	}

	const placeholderImageSrc = images[0]?.uri
		? addImageParams(images[0].uri)
		: null;

	// This class is used regardless of whether there are multiple images (carousel) or a single image (no carousel)
	const containerClass = 'vehicle-card-media-container-no-carousel';

	const vehicleImage = (
		<div
			className={setClassNames([
				containerClass,
				shouldShowCompareOverImage && 'position-relative'
			])}
		>
			<VehicleImage
				image={images[0]}
				link={vehicle.link}
				uuid={vehicle.uuid}
				isLoaded={willRenderBlankPlaceholderImage}
			/>
			{shouldShowCompareOverImage && (
				<CompareButton
					uuid={vehicle.uuid}
					title={vehicle.title}
					variant={COMPARE_BUTTON_VARIANTS.OVER_IMAGE}
				/>
			)}
		</div>
	);
	let vehicleMedia = vehicleImage;

	if (!loadingDelayed) {
		// if content is a carousel with more than 1 image
		if (enableMediaCarousel && images.length > 1) {
			vehicleMedia = (
				<div
					className={setClassNames([
						isShowVideoModalButton ? 'mb-0' : containerClass,
						shouldShowCompareOverImage && 'position-relative'
					])}
				>
					<Suspense fallback={vehicleImage}>
						<Skeleton
							skeletonClasses="vehicle-image-skeleton embed-responsive"
							placeholderImage={placeholderImageSrc}
						/>
						<LazyVehicleImageCarousel
							images={images}
							isShowVideoModalButton={isShowVideoModalButton}
							link={vehicle.link}
							uuid={vehicle.uuid}
							wasTruncated={wasTruncated}
						/>
					</Suspense>
					{shouldShowCompareOverImage && (
						<CompareButton
							uuid={vehicle.uuid}
							title={vehicle.title}
							variant={COMPARE_BUTTON_VARIANTS.OVER_IMAGE}
						/>
					)}
				</div>
			);
		}
	}
	return (
		<>
			{vehicleMedia}
			{videoButton}
			{isTabbedLayout && (
				<ul
					className="list-unstyled d-flex flex-column vehicle-card-media align-items-center hidden"
					data-location={vehicleMediaLocation}
				/>
			)}
			{!isTabbedLayout && (
				<Callout callouts={vehicle.callout} vehicle={vehicle} />
			)}
		</>
	);
};

Media.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	vehicle: vehiclePropType.isRequired,
	accountPhone: PropTypes.string,
	loadingDelayed: PropTypes.bool,
	willRenderBlankPlaceholderImage: PropTypes.bool.isRequired,
	shouldShowCompareOverImage: PropTypes.bool
};

Media.defaultProps = {
	loadingDelayed: false,
	accountPhone: ''
};

export default Media;
